import { useEffect, useState } from "react";
import api from "@/admin/api";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Trash2, Archive, AlertTriangle } from "lucide-react";

const DEP_LABELS = {
  waves: "Gelombang", invitations: "Undangan", participants: "Partisipan",
  contact_records: "Kontak", response_sessions: "Sesi respons", real_responses: "Respons nyata (aktif)",
  answers: "Jawaban", score_snapshots: "Skor", export_events: "Ekspor",
  instruments: "Instrumen", instrument_versions: "Versi instrumen", published_versions: "Versi published",
  sections: "Bagian", items: "Item", options: "Opsi", scales: "Skala",
  scoring_model_versions: "Model skor", consent_versions: "Consent",
  audit_events_retained: "Audit events (dipertahankan)",
};

// Study lifecycle: dry-run first, then Retire/Arsipkan (recommended if has data) OR
// hard-delete (platform_admin only; needs exact title + "HAPUS PERMANEN" + override
// when there are active responses). Published versions stay immutable — this is a
// lifecycle action, not an unlock.
export function StudyLifecycleDialog({ id, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [phrase, setPhrase] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/admin/delete/study/${id}/dry-run`)
      .then((r) => setPlan(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Tidak dapat memuat dependensi"));
  }, [id]);

  const titleOk = plan && title === plan.confirmation_required;
  const phraseOk = plan && (!plan.delete_phrase_required || phrase === plan.delete_phrase);
  const overrideOk = plan && (!plan.active_responses || override);

  const retire = async () => {
    setBusy("retire");
    try {
      const r = await api.post(`/admin/delete/study/${id}/retire`, { confirmation: title });
      toast.success("Studi diarsipkan (Retired) — seluruh data, scoring, dan audit tetap utuh.");
      onDone?.(r.data); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pengarsipan gagal");
    } finally { setBusy(""); }
  };

  const hardDelete = async () => {
    setBusy("delete");
    try {
      const r = await api.post(`/admin/delete/study/${id}/confirm`, {
        confirmation: title, delete_phrase: phrase || undefined, override,
      });
      toast.success("Studi dihapus permanen. Backup tersimpan.");
      onDone?.(r.data); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Penghapusan gagal");
    } finally { setBusy(""); }
  };

  const deps = plan ? Object.entries(plan.dependencies).filter(([, v]) => v > 0) : [];
  const inputCls = "mt-1 w-full rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none font-mono";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="study-lifecycle-dialog">
      <div className="w-full max-w-lg rounded-comiq-lg border bg-comiq-surface-alt p-6 max-h-[90vh] overflow-y-auto" style={{ borderColor: COLORS.line }}>
        <div className="flex items-center gap-2" style={{ color: COLORS.ink }}>
          <AlertTriangle size={18} style={{ color: "#B45309" }} />
          <h2 className="text-lg font-semibold">Kelola siklus hidup studi</h2>
        </div>

        {error && <p className="mt-4 rounded-comiq-sm px-3 py-2 text-sm" style={{ background: "#FEE4E2", color: "#B42318" }} data-testid="study-lc-error">{error}</p>}

        {plan && (
          <>
            <p className="mt-2 text-sm font-medium" style={{ color: COLORS.ink }}>{plan.target.title}</p>
            <p className="text-xs" style={{ color: COLORS.textSoft }}>
              Status: {plan.target.status}{plan.target.is_pilot ? " · PILOT/Produksi" : ""}{plan.target.is_demo ? " · Demo" : ""}
            </p>

            <div className="mt-3 rounded-comiq-sm border p-3 text-sm" style={{ borderColor: COLORS.line }} data-testid="study-lc-deps">
              {deps.length === 0 && <span style={{ color: COLORS.textSoft }}>Tidak ada dependensi.</span>}
              {deps.map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5" data-testid={`study-dep-${k}`}>
                  <span style={{ color: COLORS.textSoft }}>{DEP_LABELS[k] || k}</span>
                  <span className="font-mono font-semibold" style={{ color: k === "real_responses" && v > 0 ? "#B91C1C" : COLORS.ink }}>{v}</span>
                </div>
              ))}
            </div>

            <p className="mt-3 rounded-comiq-sm px-3 py-2 text-xs" style={{ background: "#F1FAF3", color: "#15803D" }} data-testid="study-lc-note">
              Versi published tetap immutable. Menghapus/mengarsipkan studi TIDAK menghapus user, organisasi, atau data studi lain. Audit administrasi tetap disimpan.
            </p>

            <label className="mt-4 block text-xs font-semibold" style={{ color: COLORS.ink }}>
              Ketik judul studi persis: <span className="font-mono" style={{ color: "#B91C1C" }}>{plan.confirmation_required}</span>
            </label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="study-lc-title-input" />

            {/* Retire / Arsipkan */}
            {plan.retire_available && (
              <div className="mt-4 rounded-comiq-sm border p-3" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}>
                <p className="text-xs font-semibold" style={{ color: "#B45309" }}>Retire / Arsipkan {plan.recommended_action === "retire" ? "(direkomendasikan)" : ""}</p>
                <p className="mt-1 text-xs" style={{ color: COLORS.textSoft }}>Studi disembunyikan dari daftar aktif; seluruh data, scoring, dan audit dipertahankan dan tetap dapat dilihat di daftar Arsip.</p>
                <button onClick={retire} disabled={!titleOk || busy} className="btn-comiq btn-line mt-2 disabled:opacity-40" style={{ color: "#B45309", borderColor: "#FDE68A" }} data-testid="study-retire-btn">
                  <Archive size={14} /> {busy === "retire" ? "Memproses…" : "Arsipkan (Retire)"}
                </button>
              </div>
            )}

            {/* Hard delete */}
            <div className="mt-4 rounded-comiq-sm border p-3" style={{ borderColor: "#F3C7C0", background: "#FEF2F2" }}>
              <p className="text-xs font-semibold" style={{ color: "#B91C1C" }}>Hapus permanen</p>
              <p className="mt-1 text-xs" style={{ color: COLORS.textSoft }} data-testid="study-lc-warning">
                Tindakan ini tidak dapat dibatalkan setelah backup digunakan.
              </p>
              {plan.delete_phrase_required && (
                <>
                  <label className="mt-2 block text-xs font-semibold" style={{ color: COLORS.ink }}>
                    Ketik <span className="font-mono" style={{ color: "#B91C1C" }}>{plan.delete_phrase}</span>
                  </label>
                  <input value={phrase} onChange={(e) => setPhrase(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="study-lc-phrase-input" />
                </>
              )}
              {plan.active_responses && (
                <label className="mt-3 flex items-start gap-2 text-xs" style={{ color: "#B91C1C" }}>
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="mt-0.5" data-testid="study-lc-override" />
                  <span>Studi ini memiliki response aktif. Saya memahami dampaknya dan tetap ingin menghapus permanen (override).</span>
                </label>
              )}
              <button onClick={hardDelete} disabled={!titleOk || !phraseOk || !overrideOk || busy} className="btn-comiq btn-accent mt-2 disabled:opacity-40" style={{ background: "#B91C1C" }} data-testid="study-delete-confirm-btn">
                <Trash2 size={14} /> {busy === "delete" ? "Menghapus…" : "Hapus permanen"}
              </button>
            </div>
          </>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="btn-comiq btn-line" data-testid="study-lc-cancel-btn">Tutup</button>
        </div>
      </div>
    </div>
  );
}
